import { useState } from 'react';
import useGetProductListStoreAssignmentsQuery from '@/api/queries/useGetProductListStoreAssignmentsQuery';
import useGetStoresByIdsQuery from '@/api/queries/useGetStoresByIdsQuery';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import Pagination from '@/components/Pagination';
import { isStatusActive } from '@/models/Status';
import { Store } from '@/models/Store';
import ProductListEmptyState from '@/modules/product/components/ProductListEmptyState';
import ProductListErrorState from '@/modules/product/components/ProductListErrorState';
import { TrashIcon } from '@heroicons/react/24/outline';

interface ProductListStoreTableProps {
  productListId: string;
  supplierId?: string;
  stagedItems: Store[];
  onUnstage: (storeId: string) => void;
  page: number;
  onChangePage: (page: number) => void;
  onUnassign: (storeId: string) => Promise<void>;
}

const getRetailerName = (store?: Store) =>
  store?.retailer?.organization?.name ?? store?.retailer?.name ?? 'Unavailable';

const ProductListStoreTable = ({
  productListId,
  supplierId,
  stagedItems,
  onUnstage,
  page,
  onChangePage,
  onUnassign,
}: ProductListStoreTableProps) => {
  const { assignments, itemsPerPage, isLoading, error, invalidateQuery } =
    useGetProductListStoreAssignmentsQuery(productListId, page);
  const activeAssignments = assignments.items.filter((assignment) => isStatusActive(assignment));
  const persistedStoreIds = activeAssignments.map((assignment) => assignment.storeId);
  const {
    stores,
    isLoading: storesLoading,
    error: storesError,
    invalidateQuery: invalidateStores,
  } = useGetStoresByIdsQuery(persistedStoreIds, supplierId);
  const storesById = new Map(stores.map((store) => [store.id, store]));
  const [unassigningId, setUnassigningId] = useState<string | null>(null);

  if (error || storesError) {
    return (
      <ProductListErrorState
        message="Unable to load the assigned stores."
        onRetry={() => void Promise.all([invalidateQuery(), invalidateStores()])}
      />
    );
  }

  const loading = isLoading || (persistedStoreIds.length > 0 && storesLoading);

  if (!loading && stagedItems.length === 0 && activeAssignments.length === 0) {
    return (
      <ProductListEmptyState
        title="No stores assigned to this list yet"
        description="Search for connected stores above to add one to this Product List."
      />
    );
  }

  const handleUnassign = async (storeId: string) => {
    setUnassigningId(storeId);
    try {
      await onUnassign(storeId);
      if (activeAssignments.length === 1 && page > 1) onChangePage(page - 1);
    } finally {
      setUnassigningId(null);
    }
  };

  return (
    <div className="table-container">
      <div className="table-wrapper">
        <table className={loading ? 'loading' : ''} aria-label="Store assignments">
          <thead>
            <tr>
              <th>Name</th>
              <th>Retailer</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {stagedItems.map((store) => (
              <tr key={`staged-${store.id}`} className="bg-blue-50">
                <td>{store.name}</td>
                <td>{getRetailerName(store)}</td>
                <td>
                  <Button
                    variant="delete"
                    size="xs"
                    onClick={() => onUnstage(store.id)}
                    icon={<TrashIcon className="h-5 w-5" aria-hidden="true" />}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
            {activeAssignments.map((assignment) => {
              const store = storesById.get(assignment.storeId);
              return (
                <tr key={assignment.storeId}>
                  <td>{store?.name ?? 'Store unavailable'}</td>
                  <td>{getRetailerName(store)}</td>
                  <td>
                    <Confirm
                      onConfirm={() => handleUnassign(assignment.storeId)}
                      title="Remove store"
                      message={`Are you sure you want to remove "${store?.name ?? assignment.storeId}" from this Product List?`}
                      confirmText="Remove"
                      danger
                    >
                      {(handleClick, confirming) => (
                        <Button
                          onClick={handleClick}
                          loading={confirming || unassigningId === assignment.storeId}
                          variant="delete"
                          size="xs"
                          icon={<TrashIcon className="h-5 w-5" aria-hidden="true" />}
                        >
                          Remove
                        </Button>
                      )}
                    </Confirm>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {loading && (
          <div role="status" className="loader">
            <SpinnerIcon />
            <span className="sr-only">Loading assigned stores...</span>
          </div>
        )}
      </div>
      <div className="table-bottom">
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          totalCount={assignments.totalCount || 0}
          estimatedTotalCount={assignments.estimatedTotalCount || 0}
          onClick={onChangePage}
        />
      </div>
    </div>
  );
};

export default ProductListStoreTable;
